import { useEffect } from "react";
import { useQueryString } from "src/design-system/core/hooks";
import { useAuth } from "src/hooks";
import { PATH_ACCOUNT } from "src/routesConstants";

export const REPORTS_TAB_QS_KEY = "reports-tab";

export enum ReportsTabEnum {
    Newsletter = "newsletter",
    Operational = "operational",
    Error = "error",
}

export const accountRoutes = {
    profileView: (): string => `${PATH_ACCOUNT}/profile`,
    passwordView: (): string => `${PATH_ACCOUNT}/password`,
    reportsView: (): string => `${PATH_ACCOUNT}/reports`,
};

export function useUserHasReportAccess(): boolean {
    const { user } = useAuth();

    // Disable by orga id list
    const orgIdsBlacklist = !(import.meta.env.VITE_REPORTS_ORGA_IDS_BLACKLIST ?? "")
        .split(",")
        .map(Number)
        .includes(user.organization.id);

    return orgIdsBlacklist;
}

export function useUserHasNewsletterReportAccess(): boolean {
    const { user } = useAuth();

    // Disable by country code list
    const allowNewsletterReport = !(
        import.meta.env.VITE_NEWSLETTER_REPORT_COUNTRY_CODE_BLACKLIST ?? ""
    )
        .split(",")
        .map(String)
        .includes(user.organization.countryCode);

    return allowNewsletterReport;
}

export function useReportsTab(): {
    reportsTab: ReportsTabEnum;
    setReportsTab: (reportsTab: ReportsTabEnum) => void;
} {
    const hasNewsletterReportAccess = useUserHasNewsletterReportAccess();
    const defaultValue = hasNewsletterReportAccess
        ? ReportsTabEnum.Newsletter
        : ReportsTabEnum.Operational;
    const [reportsTab, setReportsTab] = useQueryString(REPORTS_TAB_QS_KEY, defaultValue);

    let currentReportsTab = reportsTab ?? defaultValue;
    if (!hasNewsletterReportAccess && currentReportsTab === ReportsTabEnum.Newsletter) {
        currentReportsTab = ReportsTabEnum.Operational;
    }

    useEffect(() => {
        // Check if the query string value is one of the enum values
        if (reportsTab && !Object.values(ReportsTabEnum).includes(reportsTab as ReportsTabEnum)) {
            setReportsTab(defaultValue);
        }
    }, [defaultValue, reportsTab, setReportsTab]);

    return {
        reportsTab: currentReportsTab,
        setReportsTab,
    };
}
